import { Zap } from 'react-feather'
import { ChargeChart } from './ChargeSessionChart.utils'
import { useMeasuredWidth } from './useMeasuredWidth'

// Figma draws the chart at 287×185. The SVG is drawn at the card's real
// width (see useMeasuredWidth) so text and line weights never scale up.
const DESIGN_WIDTH = 287
const HEIGHT = 185
const PLOT_LEFT = 42.5
const Y_100 = 38.3
const Y_0 = 133.5
const STRIP_Y = 150
const STRIP_HEIGHT = 4.69
const X_LABEL_Y = 182
const PILL_HEIGHT = 26
const LINE_WIDTH = 1.81
const DASH = '3.63 3.63'
const MARKER_SIZE = 12
// The "now" marker sits on coloured strips, so it gets its own white disc with
// a dark rim and a dark bolt: 12:1 inside the disc, 6:1+ at the rim against
// either strip colour (WCAG 1.4.11 asks for 3:1)
const MARKER_DISC_RADIUS = 10

// SVG attributes can't take Tailwind classes, so use the themed CSS variables
const COLORS = {
  // green-700 clears 3:1 on white; green-500 is 1.9:1
  charging: 'var(--color-green-700)',
  notCharging: 'var(--color-yellow-500)',
  target: 'var(--color-purple-500)',
  gridline: 'var(--color-grey-100)',
  label: 'var(--color-grey-500)',
  marker: 'var(--color-grey-900)',
  pillBackground: 'var(--color-blue-100)',
  pillText: 'var(--color-blue-500)',
}

const LABEL_STYLE = {
  fontSize: 10.886,
  fontWeight: 700,
  fill: COLORS.label,
}

function yPosition(percent: number) {
  const clamped = Math.min(Math.max(percent, 0), 100)
  return Y_0 - (clamped / 100) * (Y_0 - Y_100)
}

type Props = {
  chart: ChargeChart
}

export default function ChargeSessionChart({ chart }: Props) {
  const { ref, width } = useMeasuredWidth<HTMLDivElement>(DESIGN_WIDTH)
  const plotRight = width - 1
  // Without axis labels (a charger's chart) the plot can use the label gutter
  const plotLeft = chart.yLabels.some(Boolean) ? PLOT_LEFT : 6
  const xPosition = (fraction: number) =>
    plotLeft + fraction * (plotRight - plotLeft)
  const targetY = yPosition(chart.targetPercent)
  // Roughly 6.3px per character at 11.2px bold Inter, plus padding
  const pillWidth = chart.targetLabel ? chart.targetLabel.length * 6.3 + 8 : 0
  const pillX =
    chart.targetX === null
      ? 0
      : Math.min(
          Math.max(xPosition(chart.targetX) - pillWidth / 2, plotLeft),
          plotRight - pillWidth,
        )

  return (
    <div ref={ref} className="w-full">
      <svg
        id="charge-forecast-v2-chart"
        role="img"
        aria-label={[
          chart.targetLabel
            ? `Battery forecast, reaching ${chart.targetLabel}`
            : 'Battery forecast',
          chart.nowLabel ? `now is ${chart.nowLabel}` : null,
        ]
          .filter(Boolean)
          .join('; ')}
        viewBox={`0 0 ${width} ${HEIGHT}`}
        className="block w-full h-auto overflow-visible"
        style={{ fontFamily: 'inherit' }}
      >
        {[100, 50, 0].map((percent, index) => (
          <text
            key={percent}
            x={31}
            y={yPosition(percent) + 4}
            textAnchor="end"
            {...LABEL_STYLE}
          >
            {chart.yLabels[index]}
          </text>
        ))}

        <line
          x1={plotLeft}
          x2={plotRight}
          y1={yPosition(50)}
          y2={yPosition(50)}
          stroke={COLORS.gridline}
          strokeWidth={LINE_WIDTH}
          strokeDasharray={DASH}
        />
        {chart.showTarget && (
          <line
            x1={plotLeft}
            x2={plotRight}
            y1={targetY}
            y2={targetY}
            stroke={COLORS.target}
            strokeWidth={LINE_WIDTH}
            strokeDasharray={DASH}
          />
        )}

        {chart.lines.map((line, index) => (
          <polyline
            key={index}
            fill="none"
            stroke={line.charging ? COLORS.charging : COLORS.notCharging}
            strokeWidth={LINE_WIDTH}
            strokeLinecap="round"
            strokeLinejoin="round"
            // Paused runs are also dashed, so the two aren't told apart by
            // colour alone
            strokeDasharray={line.charging ? undefined : '4 3'}
            points={line.points
              .map(
                (point) => `${xPosition(point.x)},${yPosition(point.percent)}`,
              )
              .join(' ')}
          />
        ))}

        <text
          x={plotLeft}
          y={Math.min(yPosition(chart.startPercent) + 13, Y_0 + 13)}
          {...LABEL_STYLE}
        >
          {chart.startLabel}
        </text>

        {chart.targetLabel && (
          <g>
            <rect
              x={pillX}
              y={0}
              width={pillWidth}
              height={PILL_HEIGHT}
              rx={7.48}
              fill={COLORS.pillBackground}
            />
            <text
              x={pillX + pillWidth / 2}
              y={17}
              textAnchor="middle"
              fontSize={11.218}
              fontWeight={700}
              fill={COLORS.pillText}
            >
              {chart.targetLabel}
            </text>
          </g>
        )}

        {chart.strip.map((piece, index) => (
          <rect
            key={index}
            x={xPosition(piece.startX)}
            y={STRIP_Y}
            width={Math.max(
              (piece.endX - piece.startX) * (plotRight - plotLeft) - 1.8,
              1,
            )}
            height={STRIP_HEIGHT}
            rx={STRIP_HEIGHT / 2}
            fill={piece.charging ? COLORS.charging : COLORS.notCharging}
          />
        ))}

        {chart.nowX !== null && (
          <g aria-hidden>
            <circle
              cx={xPosition(chart.nowX)}
              cy={STRIP_Y + STRIP_HEIGHT / 2}
              r={MARKER_DISC_RADIUS}
              fill="white"
              stroke={COLORS.marker}
              strokeWidth={1.5}
            />
            <Zap
              x={xPosition(chart.nowX) - MARKER_SIZE / 2}
              y={STRIP_Y + STRIP_HEIGHT / 2 - MARKER_SIZE / 2}
              width={MARKER_SIZE}
              height={MARKER_SIZE}
              fill={COLORS.marker}
              color={COLORS.marker}
              strokeWidth={1}
            />
          </g>
        )}

        {chart.xLabels.map((label, index) => (
          <text
            key={`${label}-${index}`}
            x={xPosition(index / (chart.xLabels.length - 1))}
            y={X_LABEL_Y}
            textAnchor={
              index === 0
                ? 'start'
                : index === chart.xLabels.length - 1
                ? 'end'
                : 'middle'
            }
            {...LABEL_STYLE}
          >
            {label}
          </text>
        ))}
      </svg>
    </div>
  )
}
